import { Card, Button, CardContent, CardActions, Box } from "@mui/material";
import { useTranslation } from "react-i18next";

import { getServiceConfig } from "@/common/serviceConfigurations";
import { assignableAppPermissions } from "@/components/UserManagement/permissionTree";
import {
  Permission,
  UserPermission,
  UsersListFilter,
} from "@/components/UserManagement/types";
import { userHasPermission } from "@/components/UserManagement/utils";
import { useDevEnvironment } from "@/contexts/DevContext";
import type { IOrgUser } from "@/types";

interface ApplicationAccessCardsProps {
  orgId: string;
  currentTabUsers: IOrgUser[];
  activeFilter: UsersListFilter | null;
  onSelectFilter: (filter: UsersListFilter) => void;
}

interface AppAccessCardProps {
  app: UserPermission;
  users: IOrgUser[];
  selectedPermission?: Permission;
  onSelectPermission: (permission: Permission) => void;
}

const LINK_BUTTON_SX = {
  textTransform: "none",
  "&:hover": {
    textDecoration: "underline",
    backgroundColor: "transparent",
  },
  "&:active": {
    backgroundColor: "transparent",
  },
} as const;

const AppAccessCard = ({
  app,
  users,
  selectedPermission,
  onSelectPermission,
}: AppAccessCardProps) => {
  const { t } = useTranslation("common");
  const countUsersWith = (permission: Permission) =>
    users.filter((user) => userHasPermission(user, permission)).length;
  const isAppSelected = selectedPermission === app.permission;

  return (
    <Card variant="outlined" sx={{ width: "220px", pb: "0.4rem" }}>
      <CardContent sx={{ lineHeight: 1, fontWeight: "bold", pb: "0" }}>
        {t(app.shortLabelKey ?? app.labelKey)}
      </CardContent>
      <CardActions
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <Button
          onClick={() => onSelectPermission(app.permission)}
          aria-pressed={isAppSelected}
          variant="text"
          disableRipple
          sx={{
            ...LINK_BUTTON_SX,
            lineHeight: 1,
            color: isAppSelected ? "primary.main" : "text.primary",
          }}
        >
          {t("userManagement.accessOverview.usersOfTotal", {
            count: countUsersWith(app.permission),
            total: users.length,
          })}
        </Button>
        {app.subPermissions?.map((subPermission) => {
          const isSelected = selectedPermission === subPermission.permission;

          return (
            <Button
              key={subPermission.permission}
              onClick={() => onSelectPermission(subPermission.permission)}
              aria-pressed={isSelected}
              variant="text"
              disableRipple
              sx={{
                ...LINK_BUTTON_SX,
                // Long labels wrap: keep them left-aligned beside the count,
                // not centered (MUI's button default).
                justifyContent: "flex-start",
                alignItems: "flex-start",
                textAlign: "left",
                py: 0.15,
                lineHeight: 1.15,
                color: isSelected ? "primary.main" : "text.secondary",
                fontSize: "0.8rem",
                fontWeight: 400,
              }}
            >
              <Box
                sx={{
                  display: "inline-block",
                  minWidth: "2ch",
                  textAlign: "right",
                  mr: 0.75,
                  fontWeight: "bold",
                  color: isSelected ? "primary.main" : "text.primary",
                }}
              >
                {countUsersWith(subPermission.permission)}
              </Box>
              <Box>{t(subPermission.labelKey)}</Box>
            </Button>
          );
        })}
      </CardActions>
    </Card>
  );
};

export default function ApplicationAccessCards(
  props: ApplicationAccessCardsProps,
) {
  const { isDevEnv } = useDevEnvironment();
  const organizationConfig = getServiceConfig(props.orgId, isDevEnv);
  const organizationAppPermissions = (organizationConfig?.apps ??
    []) as string[];
  const selectedPermission =
    props.activeFilter?.kind === "permission"
      ? props.activeFilter.permission
      : undefined;

  return (
    <div className="cards_app">
      {assignableAppPermissions(organizationAppPermissions)
        .filter(
          (app) =>
            app.permission !== Permission.CAMERA_REGISTRY &&
            organizationAppPermissions.includes(app.permission),
        )
        .map((app) => (
          <AppAccessCard
            key={app.permission}
            app={app}
            users={props.currentTabUsers}
            selectedPermission={selectedPermission}
            onSelectPermission={(permission) =>
              props.onSelectFilter({ kind: "permission", permission })
            }
          />
        ))}
    </div>
  );
}
